import { StandardEvents } from '@shopify/events';
import { syncGiftAfterAdd } from '@theme/buy-x-get-y-sync';

/**
 * Adds the Buy X Get Y gift when a shopper adds a trigger product **from a product card** — quick
 * add on a collection grid, a featured-collection row, search results, recommendations — instead of
 * from the product page.
 *
 * `blocks/buy-x-get-y.liquid` (and with it `<buy-x-get-y-auto-add>`, assets/buy-x-get-y.js) only
 * ever renders inside `templates/product.json`, so on every other surface there was nothing in the
 * DOM listening for the add and the trigger product landed in the cart with no gift beside it.
 * This module is the same idea without the banner: `snippets/product-card.liquid` resolves the
 * gift variant server-side onto each card as `data-gift-variant-id`, and this reads it back.
 *
 * Loaded from `snippets/product-card.liquid`, and only by cards that actually carry a gift — a
 * page with no such product never fetches it. Repeating the same module URL across several cards
 * is harmless: the browser's module map keys on URL, so it is fetched and evaluated exactly once.
 *
 * ⚠️ On the product page both this and `<buy-x-get-y-auto-add>` can be present at once — the PDP
 * renders the block, and product recommendations below it render cards for the same product. Both
 * react to the same event, so this one stands down whenever the block is on the page for that
 * product (see `#hasProductPageHandler`). Without that, two callers would race the same add.
 */

/**
 * ⚠️ Presence of the element is NOT enough, and assuming it was is what made the first cut of this
 * file do nothing at all on a collection page. Opening the quick-add modal pulls in the product
 * page's own blocks — `<buy-x-get-y-auto-add>` and its `<script>` tag included — but a script
 * inserted into the DOM after parsing never executes, so the class is never registered and the
 * element sits there inert: no listener, no gift. Standing down for it meant nobody handled the
 * add. Requiring the element to be a real instance of a registered class distinguishes a live
 * product-page component (stand down, it will add the gift) from that inert copy (take over).
 *
 * @param {string} productId
 * @returns {boolean} Whether a *working* product-page auto-add block already handles this product.
 */
function hasProductPageHandler(productId) {
  const element = document.querySelector(`buy-x-get-y-auto-add[data-trigger-product-id="${productId}"]`);
  const registered = customElements.get('buy-x-get-y-auto-add');
  return Boolean(element && registered && element instanceof registered);
}

/**
 * @param {string} productId
 * @returns {string | null} The gift variant a card on this page resolved for the product, if any.
 */
function findGiftVariantId(productId) {
  const card = document.querySelector(`product-card[data-product-id="${productId}"][data-gift-variant-id]`);
  return card instanceof HTMLElement ? card.dataset.giftVariantId ?? null : null;
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // 'product' is what assets/product-form.js dispatches for every add-to-cart, wherever the form
  // lives. The sync's own corrections use 'standard-action', so this can never re-trigger itself.
  if (event.action !== 'add' || event.context !== 'product') return;

  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;

      const productId = String(detail?.productId ?? '');
      if (!productId || hasProductPageHandler(productId)) return;

      // The card only gates *whether* this product is a trigger; which gift, and whether the cart has
      // earned it yet, is the rule's business (assets/gwp-engine.js via the sync).
      if (!findGiftVariantId(productId)) return;

      syncGiftAfterAdd(document.body, productId, detail?.items);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[buy-x-get-y-card-sync] Event promise rejected:', error);
    });
});
